// Selected-month balances, grouped by who owns the account. Card accounts also show where their statement
// cycle has got to, which is the one thing about a card that a balance alone does not tell you.
export interface AccountsViewProps {
  data: DashboardData;
  owner: Owner | "All";
  today: string;
  month: string;
  onRefresh: () => Promise<unknown>;
  onOpenAccount: (account: Account) => void;
  onOpenTrip?: (tripId: string) => void;
  onOpenGoal?: (goalId: string) => void;
}
import AccountOrderCard from "./AccountOrderCard";
import { useAccountOrder } from "./use-account-order";
import { useState } from "react";
import { CheckCircle2, ChevronDown, Plane } from "lucide-react";
import type { Account, DashboardData, Owner, Trip } from "../../types";
import { accountBalanceAtMonthEnd } from "../../lib/account-balances";
import { reconciliationMonthLabel } from "../../lib/reconciliation";
import Avatar from "../../components/Avatar";
import { useProfileNames } from "../../components/ProfilesContext";
import { classifyAccountForView, getAccountTrip } from "../../lib/trips";
import AccountCard from "./AccountCard";

export default function AccountsView({ data, owner, month, today, onRefresh, onOpenAccount, onOpenTrip, onOpenGoal: _onOpenGoal }: AccountsViewProps) {
  const order = useAccountOrder(data.accounts, onRefresh);
  const profileNames = useProfileNames();
  const [expandedLeftover, setExpandedLeftover] = useState<Record<string, boolean>>({});
  const [showSettled, setShowSettled] = useState<Record<string, boolean>>({});
  const [tripOnly, setTripOnly] = useState(false);

  const activeTrip = (data.trips || []).find((t) => t.status === "active");
  const historical = month !== today.slice(0, 7);

  const renderAccountCard = (account: Account, isLeftover = false, isSettled = false) =>
    <AccountCard account={account} data={data} month={month} historical={historical} isLeftover={isLeftover}
      isSettled={isSettled} onOpenAccount={onOpenAccount} onOpenTrip={onOpenTrip} />;

  return (
    <>
      {order.message ? (
        <p className="settings-note" role="status">{order.message}</p>
      ) : (
        <span role="status" className="sr-only" aria-live="polite" />
      )}
      {activeTrip && (
        <div className="accounts-trip-filter-row" role="tablist" aria-label="Filter accounts by trip">
          <button
            type="button"
            className={`accounts-filter-pill ${!tripOnly ? "active" : ""}`}
            onClick={() => setTripOnly(false)}
            role="tab"
            aria-selected={!tripOnly}
          >
            All accounts
          </button>
          <button
            type="button"
            className={`accounts-filter-pill ${tripOnly ? "active" : ""}`}
            onClick={() => setTripOnly(true)}
            role="tab"
            aria-selected={tripOnly}
          >
            <Plane size={13} />
            <span>{activeTrip.name}</span>
          </button>
        </div>
      )}
      {historical && <p className="settings-note">Balances at the end of {reconciliationMonthLabel(month)}. Goal allocations include contributions through that month. Credit limits and goal settings use your current configuration.</p>}
      {profileNames
        .filter((o) => owner === "All" || owner === o)
        .map((o) => {
          const ownerAccounts = order.accounts.filter((a) => a.owner === o);
          const mainAccounts: Account[] = [];
          const leftoverTripWallets: Account[] = [];
          const settledWallets: Account[] = [];

          for (const a of ownerAccounts) {
            const classification = classifyAccountForView({ ...a, balance: accountBalanceAtMonthEnd(a, data.transactions, month) }, data.trips);
            if (classification === "main") {
              mainAccounts.push(a);
            } else if (classification === "leftover") {
              leftoverTripWallets.push(a);
            } else {
              settledWallets.push(a);
            }
          }

          const everydayAccounts = mainAccounts.filter((a) => !getAccountTrip(a, data.trips));
          const tripAccounts = mainAccounts.filter((a) => Boolean(getAccountTrip(a, data.trips)));

          // Group trip accounts by trip
          const tripGroups: { trip: Trip; accounts: Account[] }[] = [];
          for (const a of tripAccounts) {
            const tr = getAccountTrip(a, data.trips)!;
            let group = tripGroups.find((g) => g.trip.id === tr.id);
            if (!group) {
              group = { trip: tr, accounts: [] };
              tripGroups.push(group);
            }
            group.accounts.push(a);
          }

          const visibleCount = tripOnly
            ? tripAccounts.filter((a) => getAccountTrip(a, data.trips)?.id === activeTrip?.id).length
            : mainAccounts.length;

          return (
            <section className="account-owner-group" key={o}>
              <div className="owner-heading">
                <Avatar owner={o} size={34} />
                <h2>{o}’s accounts</h2>
                <span>{visibleCount}</span>
              </div>

              {tripOnly ? (
                (() => {
                  const activeTripAccs = tripAccounts.filter((a) => getAccountTrip(a, data.trips)?.id === activeTrip?.id);
                  if (activeTripAccs.length === 0) {
                    return <p className="empty-state-muted">No accounts in {activeTrip?.name} for {o}.</p>;
                  }
                  return (
                    <div className="accounts-grid">
                      {activeTripAccs.map((a) => (
                        <AccountOrderCard key={a.id} account={a} group={`${o}-trip-${activeTrip?.id}`} siblings={activeTripAccs} order={order}>
                          {renderAccountCard(a)}
                        </AccountOrderCard>
                      ))}
                    </div>
                  );
                })()
              ) : (
                <>
                  {everydayAccounts.length > 0 ? (
                    <div className="accounts-grid">
                      {everydayAccounts.map((a) => (
                        <AccountOrderCard key={a.id} account={a} group={`${o}-main`} siblings={everydayAccounts} order={order}>
                          {renderAccountCard(a)}
                        </AccountOrderCard>
                      ))}
                    </div>
                  ) : tripAccounts.length === 0 ? (
                    <p className="empty-state-muted">No active accounts for {o}.</p>
                  ) : null}

                  {tripGroups.map(({ trip, accounts: tripAccs }) => (
                    <div key={trip.id} className="account-trip-group">
                      <div className="account-trip-group-heading">
                        <div className="account-trip-group-title">
                          <Plane size={14} />
                          <span>{trip.name}</span>
                          {trip.status === "active" && <span className="trip-status-pill active">Active</span>}
                        </div>
                        <span className="subtle-tag">{tripAccs.length}</span>
                      </div>
                      <div className="accounts-grid">
                        {tripAccs.map((a) => (
                          <AccountOrderCard key={a.id} account={a} group={`${o}-trip-${trip.id}`} siblings={tripAccs} order={order}>
                            {renderAccountCard(a)}
                          </AccountOrderCard>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              )}

              {leftoverTripWallets.length > 0 && (
                <div className="leftover-wallets-section">
                  <button
                    type="button"
                    className="leftover-wallets-toggle"
                    onClick={() =>
                      setExpandedLeftover((prev) => ({
                        ...prev,
                        [o]: !prev[o],
                      }))
                    }
                    aria-expanded={expandedLeftover[o] ?? false}
                  >
                    <div className="leftover-toggle-head">
                      <span className="leftover-icon">
                        <Plane size={15} />
                      </span>
                      <span className="leftover-title">
                        <strong>Leftover trip cash</strong>
                        <span className="subtle-tag">
                          {leftoverTripWallets.length}
                        </span>
                      </span>
                    </div>
                    <div className="leftover-toggle-meta">
                      <span className="leftover-hint">From completed trips</span>
                      <ChevronDown
                        size={16}
                        className={
                          "leftover-chevron " +
                          (expandedLeftover[o] ? "open" : "")
                        }
                      />
                    </div>
                  </button>

                  {expandedLeftover[o] && (
                    <div className="accounts-grid leftover-accounts-grid">
                      {leftoverTripWallets.map((a) =>
                        <AccountOrderCard key={a.id} account={a} group={`${o}-leftover`} siblings={leftoverTripWallets} order={order}>{renderAccountCard(a, true)}</AccountOrderCard>
                      )}
                    </div>
                  )}
                </div>
              )}

              {settledWallets.length > 0 && (
                <div className="settled-wallets-container">
                  <button
                    type="button"
                    className="settled-wallets-btn"
                    onClick={() =>
                      setShowSettled((prev) => ({
                        ...prev,
                        [o]: !prev[o],
                      }))
                    }
                  >
                    <CheckCircle2 size={13} />
                    <span>
                      {settledWallets.length} closed trip wallet
                      {settledWallets.length === 1 ? "" : "s"} settled
                    </span>
                    <span className="settled-toggle-action">
                      {showSettled[o] ? "Hide" : "Show"}
                    </span>
                  </button>
                  {showSettled[o] && (
                    <div className="accounts-grid settled-grid">
                      {settledWallets.map((a) =>
                        <AccountOrderCard key={a.id} account={a} group={`${o}-settled`} siblings={settledWallets} order={order}>{renderAccountCard(a, false, true)}</AccountOrderCard>
                      )}
                    </div>
                  )}
                </div>
              )}
            </section>
          );
        })}
    </>
  );
}
